import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api, formatBRL } from "@/lib/api";
import { Check, Copy, MessageCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

export default function OrderConfirmedPage() {
  const { code } = useParams();
  const [order, setOrder] = useState(null);
  const [wa, setWa] = useState("5511999999999");

  useEffect(() => {
    api.get(`/orders/${code}`).then((r) => setOrder(r.data));
    api.get("/settings").then((r) => setWa(r.data?.whatsapp_number || "5511999999999"));
  }, [code]);

  if (!order) {
    return <div className="mx-auto max-w-3xl px-5 py-24 text-zinc-400">Carregando pedido...</div>;
  }

  const copy = (txt) => {
    navigator.clipboard.writeText(txt);
    toast.success("Copiado!");
  };

  const waMessage = encodeURIComponent(`Olá! Acabei de fazer o pedido ${order.code} na ActiveMode. Pagamento: ${order.payment_method === "pix" ? "Pix" : "Cartão de Crédito"}. Total: ${formatBRL(order.total)}`);

  return (
    <div className="mx-auto max-w-3xl px-5 lg:px-8 py-14">
      <div className="h-14 w-14 rounded-full bg-emerald-500/10 border border-emerald-400/40 grid place-items-center mx-auto">
        <Check className="text-emerald-400" size={26} />
      </div>
      <h1 className="font-display text-4xl sm:text-5xl tracking-tighter text-white mt-6 text-center" data-testid="order-code">Pedido confirmado</h1>
      <p className="text-center text-zinc-400 mt-3">Nº {order.code} — {order.customer.name}</p>

      <div className="mt-10 border border-white/10 p-6 lg:p-8">
        <div className="overline-label">Pagamento</div>
        {order.payment_method === "pix" ? (
          <div className="mt-5">
            <p className="text-white font-display text-xl">Pague com Pix para liberar o envio</p>
            <p className="text-zinc-400 text-sm mt-2">{order.payment_demo?.instructions}</p>
            <div className="mt-5 bg-[#0A0A0C] border border-white/10 p-4 text-xs text-zinc-300 break-all font-mono">
              {order.payment_demo?.qr_code_text}
            </div>
            <Button onClick={() => copy(order.payment_demo?.qr_code_text)} variant="outline" className="mt-4 bg-transparent border-white/20 text-white hover:bg-white/5">
              <Copy size={14} className="mr-2" /> Copiar código Pix
            </Button>
          </div>
        ) : (
          <p className="mt-4 text-zinc-300 text-sm">Pagamento com cartão em processamento. {order.payment_demo?.instructions}</p>
        )}
      </div>

      <div className="mt-6 border border-white/10 p-6 lg:p-8">
        <div className="overline-label">Itens</div>
        <div className="mt-5 divide-y divide-white/5">
          {order.items.map((it, i) => (
            <div key={i} className="flex justify-between py-3 text-sm">
              <div>
                <div className="text-white">{it.name} <span className="text-zinc-500">× {it.quantity}</span></div>
                <div className="text-xs text-zinc-500">{[it.size && `Tam ${it.size}`, it.color && `Cor ${it.color}`].filter(Boolean).join(" · ")}</div>
              </div>
              <div className="text-white font-display">{formatBRL(it.line_total)}</div>
            </div>
          ))}
        </div>
        <div className="mt-5 pt-4 border-t border-white/10 space-y-2 text-sm">
          <Row label="Subtotal" value={formatBRL(order.subtotal)} />
          <Row label="Frete" value={order.shipping === 0 ? "Grátis" : formatBRL(order.shipping)} />
          <Row label="Total" value={formatBRL(order.total)} bold />
        </div>
      </div>

      <div className="mt-6 border border-white/10 p-6 lg:p-8">
        <div className="overline-label">Endereço de entrega</div>
        <p className="mt-4 text-sm text-zinc-300 leading-relaxed">
          {order.address.street}, {order.address.number} {order.address.complement && `· ${order.address.complement}`} <br />
          {order.address.neighborhood} — {order.address.city}/{order.address.state} · CEP {order.address.cep}
        </p>
      </div>

      <div className="mt-8 flex flex-col sm:flex-row gap-3 justify-center">
        <Button asChild className="bg-white text-black hover:bg-zinc-200 h-12 px-8 uppercase tracking-widest text-xs">
          <Link to="/">Continuar comprando</Link>
        </Button>
        <Button asChild variant="outline" className="bg-transparent border-white/20 text-white hover:bg-white/5 h-12 px-8">
          <a href={`https://wa.me/${wa.replace(/\D/g,"")}?text=${waMessage}`} target="_blank" rel="noreferrer" data-testid="order-whatsapp-btn">
            <MessageCircle size={16} className="mr-2" /> Confirmar via WhatsApp
          </a>
        </Button>
      </div>
    </div>
  );
}

function Row({ label, value, bold }) {
  return (
    <div className={`flex justify-between ${bold ? "pt-2 border-t border-white/10 mt-2" : ""}`}>
      <span className="text-zinc-300">{label}</span>
      <span className={bold ? "font-display text-lg text-white" : "text-white"}>{value}</span>
    </div>
  );
}
